/* eslint-disable unicorn/prefer-array-flat */
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import { matchingAddresses } from 'react/shared/utils/Address';
import apiMapper from 'react/signups/card/ApiMapper';
import api from 'react/signups/card/api';
import ClientCardProgram from 'react/signups/card/modules/card_order/card_program/client/CardProgram';
import FamilyCardProgram from 'react/signups/card/modules/card_order/card_program/family/CardProgram';
import MyselfCardProgram from 'react/signups/card/modules/card_order/card_program/myself/CardProgram';
import ClientCardholderProfile from 'react/signups/card/modules/card_order/cardholder_profile/client/CardholderProfile';
import FamilyCardholderProfile from 'react/signups/card/modules/card_order/cardholder_profile/family/CardholderProfile';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import {
  goToNextStep,
  shortenRecipe,
  overrideNextModule,
} from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import cardProgramQuestionsShape from 'react/signups/card/shapes/CardProgramQuestions';
import organizationShape from 'react/signups/card/shapes/Organization';
import recipeShape from 'react/signups/card/shapes/Recipe';
import userShape from 'react/signups/card/shapes/User';
import { yesNoToBoolean, booleanToYesNo, convertDate, getCardName } from 'react/signups/card/utils';

export default function CardOrder({
  account,
  cardAccountSignup,
  cardProgramQuestions,
  organization,
  recipe,
  setAccount,
  setCardProgramQuestions,
  user,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;

  // -----------------------------------------------------------------------------------------------

  // Arbiters of initial state ---------------------------------------------------------------------

  const sameAdminAndCardholderAddress = useMemo(() => {
    if (userType !== USER_TYPES.FAMILY) return '';
    if (!account.cardholder?.address) return 'true';

    return matchingAddresses(organization.address, account.cardholder?.address).toString();
  }, [account, organization, userType]);

  // -----------------------------------------------------------------------------------------------

  // State hooks -----------------------------------------------------------------------------------

  // Module-level navigation state
  const [moduleComplete, setModuleComplete] = useState(false);

  const [rebuildCount, setRebuildCount] = useState(0);

  // Module-level unpersisted form state
  const [cardProgramData, setCardProgramData] = useState({
    atmAccess: booleanToYesNo(account.atmAccess) || '',
    fundedByTrust: booleanToYesNo(account.fundedByTrust) || '',
  });

  const [cardholderProfileData, setCardholderProfileData] = useState({
    firstName: account.cardholder?.firstName || '',
    lastName: account.cardholder?.lastName || '',
    address: {
      street1: account.cardholder?.address?.street1 || '',
      street2: account.cardholder?.address?.street2 || '',
      city: account.cardholder?.address?.city || '',
      state: account.cardholder?.address?.state || '',
      zip: account.cardholder?.address?.zip || '',
    },
    sameAddress: sameAdminAndCardholderAddress,
    dateOfBirth: convertDate(account.cardholder?.dob) || '',
    mobile: account.cardholder?.mobile || '',
    countryOfCitizenship: account.cardholder?.countryOfCitizenship || '',
    nonResidentAlien: booleanToYesNo(account.cardholder?.nonResidentAlien) || '',
  });

  const buildRecipe = useCallback(() => {
    const skipPaths = [].concat(
      !cardProgramQuestions.atm_access
        ? ['/card_order/card_program'] // if atm_access disabled, remove card program step
        : [],
    );

    const shortenedRecipe = shortenRecipe({ skipPaths, recipe, userType });

    let nextModule = recipe.variants[userType].next;

    if (userType === USER_TYPES.CLIENT) {
      if (account.fundedByTrust) {
        nextModule = recipe.variants[userType].alternativeNext.sntFlow;
      }
    } else if (userType === USER_TYPES.FAMILY) {
      if (account.fundedByTrust || cardAccountSignup?.cardFor?.includes('teen-or-child')) {
        nextModule = recipe.variants[userType].alternativeNext.sntFlow;
      } else if (cardholderProfileData.sameAddress === 'true') {
        nextModule = recipe.variants[userType].alternativeNext.sameAddress;
      }
    }

    return overrideNextModule({
      nextModule,
      recipe: shortenedRecipe,
      userType,
    });
  }, [account, cardProgramQuestions, recipe, userType, cardholderProfileData, cardAccountSignup]);

  const [localRecipe, setLocalRecipe] = useState(buildRecipe());

  // -----------------------------------------------------------------------------------------------

  // Effect hooks and memoized callbacks -----------------------------------------------------------

  // Fetch new card program data on mount
  useEffect(() => {
    api.getCardProgramQuestions(organization.id).then(({ data }) => {
      setCardProgramQuestions(data.questions);
    });
  }, [organization.id, setCardProgramQuestions]);

  // Move to next step or mark module complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      moduleComplete,
      setModuleComplete,
      recipe: localRecipe,
      userType,
    });
  }, [
    location,
    localRecipe,
    navigate,
    userType,
    cardAccountSignup,
    moduleComplete,
    setModuleComplete,
  ]);

  // Skip Card Ordering and go to success page
  const onEarlyComplete = useCallback(() => {
    api.completeSignup(cardAccountSignup.id).then(() => {
      window.Truelink && window.Truelink.flashClear();
      // reset to top
      window.scrollTo(0, 0);
      navigate('/review/success?early=true');
    });
  }, [cardAccountSignup.id, navigate]);

  // Send module payload and update Application-level state
  useUpdateEffect(() => {
    if (!moduleComplete) return;

    let cardholderAddress = cardholderProfileData.address;
    let firstName = cardholderProfileData.firstName || cardAccountSignup.cardholderFirstName;
    let lastName = cardholderProfileData.lastName || cardAccountSignup.cardholderLastName;
    let shippingAddress = {};
    let shippingDestination;

    // For the myself flow, copy data from the admin profile pages
    if (userType === USER_TYPES.MYSELF) {
      cardholderAddress = organization.address;
      firstName = user.firstName;
      lastName = user.lastName;

      shippingAddress = organization.address;
      shippingDestination = 'office';
    } else if (cardholderProfileData.sameAddress === 'true') {
      shippingAddress = cardholderAddress;
      shippingDestination = 'cardholder_home';
    }

    const apiRequest = account.id ? api.updateAccount : api.createAccount;
    const apiId = account.id || organization.id;

    apiRequest(
      apiId,
      apiMapper.buildCardOrderAccountParams({
        countryOfCitizenship: cardholderProfileData.countryOfCitizenship,
        nonResidentAlien: yesNoToBoolean(cardholderProfileData.nonResidentAlien),
        cardAccountSignupId: cardAccountSignup.id,
        cardholderAddress,
        atmAccess: yesNoToBoolean(cardProgramData.atmAccess),
        fundedByTrust: yesNoToBoolean(cardProgramData.fundedByTrust),
        cardName: getCardName(firstName, lastName),
        firstName,
        lastName,
        dob: convertDate(cardholderProfileData.dateOfBirth),
        mobile: cardholderProfileData.mobile,
        shippingAddress,
        shippingDestination,
      }),
    ).then((response) => {
      setAccount(apiMapper.mapServerAccountToClientAccount(response.data.data));
    });
  }, [moduleComplete]);

  // Rebuild local recipe when Module-level state changes in response to form submit
  // Rebuild local recipe when card program questions refresh
  useUpdateEffect(() => {
    setLocalRecipe(buildRecipe());
  }, [account, cardholderProfileData, cardProgramQuestions]);

  // Rebuild local recipe when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    onNavigate();
  }, [cardProgramData]);

  // Navigate to next step when Module-level state changes in response to recipe rebuild
  // caveat: allow one free initial rebuild (when card program questions refresh)
  useUpdateEffect(() => {
    rebuildCount < 1 ? setRebuildCount(1) : onNavigate();
  }, [localRecipe]);

  // -----------------------------------------------------------------------------------------------

  // Pass-down state, props and handlers -----------------------------------------------------------

  const passDownValues = {
    cardAccountSignup,
    firstName: cardAccountSignup.cardholderFirstName || cardholderProfileData.firstName,
    cardProgramQuestions,
    cardProgramData,
    cardholderProfileData,
    moduleComplete,
    organizationAddress: organization.address,
  };

  const passDownHandlers = {
    onEarlyComplete,
    setCardProgramData,
    setCardholderProfileData,
  };

  const cardholderProfile = (
    <>
      {userType === USER_TYPES.CLIENT && (
        <ClientCardholderProfile {...passDownValues} {...passDownHandlers} />
      )}
      {userType === USER_TYPES.FAMILY && (
        <FamilyCardholderProfile {...passDownValues} {...passDownHandlers} />
      )}
    </>
  );

  const cardProgram = (
    <>
      {userType === USER_TYPES.CLIENT && (
        <ClientCardProgram {...passDownValues} {...passDownHandlers} />
      )}
      {userType === USER_TYPES.FAMILY && (
        <FamilyCardProgram {...passDownValues} {...passDownHandlers} />
      )}
      {userType === USER_TYPES.MYSELF && (
        <MyselfCardProgram {...passDownValues} {...passDownHandlers} />
      )}
    </>
  );

  // This catches any hard refresh within card order and starts the user over
  const matchProfile = useMatch('/card_order/cardholder_profile');
  if (userType !== USER_TYPES.MYSELF && !matchProfile && cardholderProfileData.dateOfBirth === '') {
    return <Navigate replace to={recipe.defaultPath} />;
  }

  return (
    <Routes>
      <Route element={cardholderProfile} path="/cardholder_profile" />
      <Route element={cardProgram} path="/card_program" />
      <Route
        element={
          <Navigate
            replace
            to={userType === USER_TYPES.MYSELF ? '/card_program' : recipe.defaultPath}
          />
        }
        path="/*"
      />
    </Routes>
  );
}

CardOrder.propTypes = {
  account: PropTypes.object,
  cardAccountSignup: cardAccountSignupShape.isRequired,
  cardProgramQuestions: cardProgramQuestionsShape.isRequired,
  organization: organizationShape.isRequired,
  recipe: recipeShape.isRequired,
  setAccount: PropTypes.func.isRequired,
  setCardProgramQuestions: PropTypes.func.isRequired,
  user: userShape.isRequired,
};
